import assertExhaustive from '@bybeam/platform-lib/utilities/assertExhaustive';
import { Field as FieldDefinition, FieldType } from '@bybeam/platform-types';
import { GenericFormData } from '@platform-ui-common/hooks/useForm/index.js';
import usePlaceholder from '@platform-ui-common/hooks/usePlaceholder.js';
import { replaceCopyPlaceholders } from '@platform-ui/utilities/application/fields/index.js';
import AddressField from './AddressField/index.js';
import CalculatedField from './CalculatedField.js';
import CheckboxField from './CheckboxField.js';
import CheckboxGroupField from './CheckboxGroupField.js';
import ComplexField from './ComplexField.js';
import DateField from './DateField.js';
import DocumentField from './DocumentField.js';
import DropdownField from './DropdownField.js';
import RadioListField from './RadioListField.js';
import TextContentField from './TextContentField.js';
import TextField from './TextField.js';
import { FieldProps } from './types.js';

export default function Field<FormData extends GenericFormData>({
  field,
  ...props
}: FieldProps<FieldDefinition, FormData>): JSX.Element {
  const placeholderData = usePlaceholder();
  const computedField = replaceCopyPlaceholders(field, placeholderData);

  switch (computedField.type) {
    case FieldType.Address:
      return <AddressField {...props} field={computedField} />;
    case FieldType.Calculated:
      if (computedField?.display) {
        return <CalculatedField {...props} disabled={true} field={computedField} />;
      }
      return null;
    case FieldType.Checkbox:
      return <CheckboxField {...props} field={computedField} />;
    case FieldType.CheckboxGroup:
      return <CheckboxGroupField {...props} field={computedField} />;
    case FieldType.Complex:
      return <ComplexField {...props} field={computedField} />;
    case FieldType.Date:
      return <DateField {...props} field={computedField} />;
    case FieldType.Dropdown:
      return <DropdownField {...props} field={computedField} />;
    case FieldType.Text:
      return <TextField {...props} field={computedField} />;
    case FieldType.RadioList:
      return <RadioListField {...props} field={computedField} />;
    case FieldType.Document:
      return <DocumentField {...props} field={computedField} />;
    case FieldType.Typography:
      return <TextContentField {...props} field={computedField} />;
    default:
      return assertExhaustive(computedField);
  }
}
